import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

export interface IStyledResizeHandleProps {
  direction?: 'horizontal' | 'vertical';
  withDivider?: boolean;
  highlightSide?: 'start' | 'end';
}

const RESIZE_HANDLE_SIZE = 1;
// The interactive grab width. The Separator element itself stays 1px (so it adds no layout gap);
// react-resizable-panels v4 widens the hit region to this value via `resizeTargetMinimumSize`
// (set in the PanelGroup wrapper), and the `::before` mirrors it for the element's own :hover.
export const RESIZE_HANDLE_HIT_AREA = 12;
const RESIZE_HANDLE_HIT_AREA_OFFSET = (RESIZE_HANDLE_HIT_AREA - RESIZE_HANDLE_SIZE) / 2;
// Soft highlight band shown on hover / while dragging — matches the navigation drawer handle.
const HIGHLIGHT_THICKNESS = 4;

export const StyledResizeHandle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'direction' && prop !== 'withDivider' && prop !== 'highlightSide',
})<IStyledResizeHandleProps>(({ theme, direction = 'horizontal', withDivider, highlightSide = 'end' }) => {
  const isHorizontal = direction === 'horizontal';
  const atEnd = highlightSide !== 'start';

  return {
    position: 'relative',
    width: isHorizontal ? RESIZE_HANDLE_SIZE : '100%',
    height: isHorizontal ? '100%' : RESIZE_HANDLE_SIZE,
    cursor: isHorizontal ? 'col-resize' : 'row-resize',
    // Thin persistent separator line (only when requested).
    backgroundColor: withDivider ? theme.palette.divider : 'transparent',
    // Invisible hit-area extender so the thin handle is easy to point at (drives the element :hover).
    '&::before': {
      content: '""',
      position: 'absolute',
      ...(isHorizontal
        ? { top: 0, bottom: 0, left: -RESIZE_HANDLE_HIT_AREA_OFFSET, width: RESIZE_HANDLE_HIT_AREA }
        : { left: 0, right: 0, top: -RESIZE_HANDLE_HIT_AREA_OFFSET, height: RESIZE_HANDLE_HIT_AREA }),
    },
    // Soft highlight band; invisible at rest, fades in on hover and stays the same while dragging.
    // Offset to one side of the separator line (not centred) so the divider sits at the band's edge
    // and never appears to cut through the glow — matching the navigation drawer handle.
    '&::after': {
      content: '""',
      position: 'absolute',
      backgroundColor: theme.palette.divider,
      opacity: 0,
      transition: 'opacity 0.2s ease',
      ...(isHorizontal
        ? { top: 0, bottom: 0, [atEnd ? 'left' : 'right']: '50%', width: HIGHLIGHT_THICKNESS }
        : { left: 0, right: 0, [atEnd ? 'top' : 'bottom']: '50%', height: HIGHLIGHT_THICKNESS }),
    },
    '&:hover::after, [data-separator="hover"] &::after, [data-separator="active"] &::after': {
      opacity: 0.5,
    },
  };
});
